import React, { useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { createMaterialCollapsibleTopTabNavigator } from 'react-native-collapsible-tab-view';
import { styled } from 'dripsy';
import { View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useNavigation } from '@react-navigation/native';

import { screens } from './screens';
import type {
  SearchTabParamListType,
  MainNavigatorNavigationPropType,
} from './types';
import { SearchTutorialScreen, SearchProfileScreen } from '@screens';
import { SCREEN_TOP_BAR_HEIGHT } from '@components';
import { SearchTabHeader } from './components/SearchTabHeader';
import { TopTabBar } from './components/TopTabBar';
import { SearchTabNavigatorProvider } from '@context';
import { IS_MOBILE } from '@utils';

const TabNavigator =
  createMaterialCollapsibleTopTabNavigator<SearchTabParamListType>();

const TabBarContainer = styled(View)({
  px: ['$6', '$7', '5%'],
  bg: '$background0',
});

type NavigatorParamsType = {
  params?: { query?: string };
  screen: string;
};

type ScreenParamsType = {
  query?: string;
};

type NavigationPropType =
  MainNavigatorNavigationPropType<'SearchTabNavigator'>;

export const SearchTabNavigator = observer(() => {
  const { t } = useTranslation();
  const nav = useNavigation<NavigationPropType>();

  const initialQuery = useMemo(() => {
    const state = nav.getState();

    const navigatorRoute = state.routes[state.index];

    if (navigatorRoute.name === screens.SearchTabNavigator) {
      if (navigatorRoute.params) {
        const params = navigatorRoute.params as NavigatorParamsType;

        return params.params?.query ?? '';
      } else {
        const screenRoute =
          navigatorRoute.state?.routes[
            navigatorRoute.state.index ?? 0
          ];
        const params = screenRoute?.params as ScreenParamsType;

        return params?.query ?? '';
      }
    }

    return '';
  }, []);

  return (
    <SearchTabNavigatorProvider>
      <TabNavigator.Navigator
        initialRouteName={screens.SearchTutorial}
        tabBar={(props) => (
          <TabBarContainer>
            <TopTabBar {...props} />
          </TabBarContainer>
        )}
        collapsibleOptions={{
          renderHeader: () => (
            <SearchTabHeader initSearchText={initialQuery} />
          ),
          headerHeight: SCREEN_TOP_BAR_HEIGHT,
          disableSnap: true,
          swipeEnabled: false,
          animationEnabled: IS_MOBILE,
        }}
      >
        <TabNavigator.Screen
          name={screens.SearchTutorial}
          component={SearchTutorialScreen}
          options={{
            title: t('search.video'),
          }}
          initialParams={{ query: initialQuery }}
        />
        <TabNavigator.Screen
          name={screens.SearchProfile}
          component={SearchProfileScreen}
          options={{
            title: t('search.profiles'),
          }}
          initialParams={{ query: initialQuery }}
        />
      </TabNavigator.Navigator>
    </SearchTabNavigatorProvider>
  );
});
